import { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from '../services/supabaseClient';

/**
 * Track real-time data source for sensors (gateway vs app)
 * Similar to SensorCard.tsx liveSource logic from SensorPush reference app
 * 
 * Returns live source state that updates IMMEDIATELY on new data via Supabase subscription
 * Also fetches initial source values on mount for instant display
 */
export const useLiveSource = (sensorIds = []) => {
  const [liveSources, setLiveSources] = useState({});
  const timeoutRefs = useRef({});
  const subscriptionRef = useRef(null);
  const initialFetchDoneRef = useRef(false);
  
  // Memoize sensor IDs to prevent unnecessary re-renders
  const memoizedSensorIds = useMemo(() => {
    return sensorIds && Array.isArray(sensorIds) ? [...sensorIds].sort() : [];
  }, [sensorIds]);

  // Fetch initial source values for all sensors on mount
  useEffect(() => {
    if (!memoizedSensorIds || memoizedSensorIds.length === 0 || initialFetchDoneRef.current) {
      return;
    }

    const fetchInitialSources = async () => {
      try {
        // Fetch latest reading source for each sensor in parallel
        const { data: latestReadings, error } = await supabase
          .from('sensor_readings')
          .select('sensor_id, source, created_at')
          .in('sensor_id', memoizedSensorIds)
          .order('created_at', { ascending: false });

        if (error) {
          console.warn('Failed to fetch initial sources:', error);
          return;
        }

        if (latestReadings && latestReadings.length > 0) {
          // Group by sensor_id and get the latest for each
          const latestBySensor = {};
          latestReadings.forEach(reading => {
            if (!latestBySensor[reading.sensor_id]) {
              latestBySensor[reading.sensor_id] = reading;
            }
          });

          // Set initial sources (not marked as active/live)
          const initialSources = {};
          Object.entries(latestBySensor).forEach(([sensorId, reading]) => {
            initialSources[sensorId] = {
              source: reading.source,
              timestamp: new Date(reading.created_at),
              isActive: false // Not actively live, just initial value
            };
          });

          setLiveSources(prev => ({ ...prev, ...initialSources }));
          console.log('✅ Initial sources loaded:', Object.keys(initialSources).length, 'sensors');
        }

        initialFetchDoneRef.current = true;
      } catch (err) {
        console.warn('Error fetching initial sources:', err);
      }
    };

    fetchInitialSources();
  }, [memoizedSensorIds]);

  useEffect(() => {
    if (!memoizedSensorIds || memoizedSensorIds.length === 0) {
      if (Object.keys(liveSources).length > 0) {
        setLiveSources({});
      }
      return;
    }

    // Subscribe to real-time sensor_readings inserts for IMMEDIATE updates
    const channel = supabase
      .channel('live_source_tracking')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sensor_readings',
        },
        (payload) => {
          const reading = payload.new;
          const sensorId = reading.sensor_id;
          const source = reading.source; // 'mqtt', 'app', 'http'
          const gatewayId = reading.gateway_id; // Present if data came from gateway

          // Only track sensors in our list
          if (!memoizedSensorIds.includes(sensorId)) {
            return;
          }

          console.log(`⚡ INSTANT source update: ${sensorId} -> source: ${source}, gateway_id: ${gatewayId}`);

          // Update live source immediately - just track isActive state
          // The actual source value (mqtt/http/app) is used directly from the database
          setLiveSources(prev => ({
            ...prev,
            [sensorId]: {
              source: source, // Store actual source (mqtt/http/app)
              timestamp: new Date(reading.created_at),
              isActive: true
            }
          }));

          // Clear existing timeout for this sensor
          if (timeoutRefs.current[sensorId]) {
            clearTimeout(timeoutRefs.current[sensorId]);
          }

          // Set timeout to mark as inactive after 30 seconds
          // This matches SensorCard.tsx logic: data is "live" if within last 30 seconds
          timeoutRefs.current[sensorId] = setTimeout(() => {
            setLiveSources(prev => {
              const updated = { ...prev };
              if (updated[sensorId]) {
                updated[sensorId] = {
                  ...updated[sensorId],
                  isActive: false
                };
              }
              return updated;
            });
          }, 30000); // 30 seconds
        }
      )
      .subscribe();

    subscriptionRef.current = channel;

    // Cleanup
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      // Clear all timeouts
      Object.values(timeoutRefs.current).forEach(timeout => clearTimeout(timeout));
      timeoutRefs.current = {};
    };
  }, [memoizedSensorIds, liveSources]); // Use memoized sensor IDs and include liveSources

  return liveSources;
};
